import { ClientConfig, createClient, SanityClient } from '@sanity/client';

import type { MinItemArray } from '@ag1/ui';
import { datadogRum } from '@datadog/browser-rum-slim';
import { LocaleEnum } from './schema/locale';
import { shouldFilterError } from './state/utils/shouldFilterError';

const SANITY_PROJECT_ID = process.env.NEXT_PUBLIC_SANITY_PROJECT_ID || '';
const SANITY_DATASET = process.env.NEXT_PUBLIC_SANITY_DATASET || '';
const SANITY_RELEASE_PREVIEW = process.env.NEXT_PUBLIC_SANITY_RELEASE_PREVIEW || '';
const SANITY_AUTH_TOKEN = process.env.NEXT_PUBLIC_SANITY_AUTH_TOKEN || '';
const SANITY_AUTH_TOKEN_API_LAYER = process.env.SANITY_AUTH_TOKEN_API_LAYER || '';

const apiVersion = (() => {
  const date = new Date();
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `v${year}-${month < 10 ? '0' : ''}${month}-${day < 10 ? '0' : ''}${day}`;
})();

const isReleasePreview = !!SANITY_RELEASE_PREVIEW;
const releasePreviewConfig = {
  useCdn: false, // Ignore CDN for release previews to fetch latest content
  token: SANITY_AUTH_TOKEN, // Non-published perspectives require auth token
  perspective: [SANITY_RELEASE_PREVIEW]
};

export const config: ClientConfig = {
  projectId: SANITY_PROJECT_ID,
  dataset: SANITY_DATASET,
  useCdn: true,
  apiVersion: '2025-07-14',
  ...(isReleasePreview ? releasePreviewConfig : {})
};

const client = createClient(config);

export type QueryParams = {
  language?: never;
  locale?: LocaleEnum | null;
  [key: string]: any;
};

const qaLocalesMap: Partial<Record<LocaleEnum, LocaleEnum>> = {
  [LocaleEnum.enDev]: LocaleEnum.enUS,
  [LocaleEnum.enQa]: LocaleEnum.enUS,
  [LocaleEnum.deQa]: LocaleEnum.enUS,
  [LocaleEnum.enAud]: LocaleEnum.enAU,
  [LocaleEnum.enNzd]: LocaleEnum.enNZ,
  [LocaleEnum.enEud]: LocaleEnum.enEU,
  [LocaleEnum.deEud]: LocaleEnum.deEU,
  [LocaleEnum.nlEU]: LocaleEnum.nlEU,
  [LocaleEnum.seEU]: LocaleEnum.seEU,
  [LocaleEnum.enUkd]: LocaleEnum.enUK,
  [LocaleEnum.esEU]: LocaleEnum.esEU,
  [LocaleEnum.frEU]: LocaleEnum.frEU
};

/**
 * Can either be a string, or a tuple of a string and an array of valid keys for
 * said string.
 *
 * Prefix with '!' to match on any value other than what is specified.
 *
 * Examples (`_type: 'block' | 'span' | 'inline'`):
 * - `['_type']` => '_type' key must be present.
 * - `['_type', ['!!']]` => '_type' must be a non-null value (equivalent to
 *   `!!object['_type']`).
 * - `['_type', ['block']]` => '_type' must be 'block'.
 * - `['_type', ['block', 'span', 'inline']]` => '_type' must be 'block',
 *   'span', or 'inline'.
 * - `['_type', ['!block']]` => '_type' must NOT be 'block' (must be 'span' or
 *   'inline').
 */
type MatchedFields = MinItemArray<string | [string, MinItemArray<string>]>;

interface SanityKeyFilter {
  /** See {@link MatchedFields} */
  matchedFields: MatchedFields;
  /** Fields to remove when {@link matchedFields} has a match. */
  toRemove: MinItemArray<string>;
}

interface SanityObjectFilter {
  /** See {@link MatchedFields} */
  matchedFields: MatchedFields;
  /**
   * Objects require at least one of these fields to be present to be deleted.
   *
   * Undefined requiredFields will always be deleted if {@link matchedFields}
   * has a match.
   */
  requiredFields?: MinItemArray<string>;
}

/**
 * Checks contents of an object to determine if the whole object should be
 * deleted.
 */
const SANITY_OBJECT_FILTERS: SanityObjectFilter[] = [
  // UnifiedButton - TODO: put this back, since it's causing issues and deleting the entire section with this button
  // {
  //   matchedFields: [
  //     // Delete noButton buttons, except...
  //     ['switchButton', ['noButton']],
  //     // Expandable list items
  //     ['_type', ['!expandableItem']]
  //   ]
  // },
  // Images/videos
  {
    matchedFields: ['position', 'fit', ['_type', ['image', 'video', 'baseImage', 'baseVideo']]],
    requiredFields: ['asset', 'video']
  }
];

/**
 * Checks contents of an object to know which fields of the object to delete.
 */
const SANITY_KEY_FILTERS: SanityKeyFilter[] = [
  // UnifiedButton
  {
    matchedFields: [['switchButton', ['!goToOfferButton']]],
    toRemove: ['goToOfferButton']
  },
  {
    matchedFields: [['switchButton', ['!scrollButton']]],
    toRemove: ['scrollButton']
  },
  {
    matchedFields: [['switchButton', ['!openModalButton']]],
    toRemove: ['openModalButton']
  },
  {
    matchedFields: [['switchButton', ['!link']]],
    toRemove: ['baseButtonLinkV2']
  },
  // [Portable Text] parent
  {
    matchedFields: ['markDefs', ['_type', ['block']], 'children'],
    toRemove: ['_key']
  },
  // [Portable Text] child
  {
    matchedFields: ['marks', ['_type', ['span']], 'text'],
    toRemove: ['_key']
  }
];

/** Array of fields/indices used to crawl object/array tree */
type Keychain = (string | number)[];

const checkMatchedField = (
  obj: Record<string, any>,
  objectKeys: string[],
  field: MatchedFields[number]
) => {
  // Check if the field is a tuple, and if so, check if the value is in the
  // array of valid values.
  if (Array.isArray(field)) {
    const [key, validValues] = field;

    return (
      objectKeys.includes(key) &&
      validValues.some((rawValue) => {
        let isExclusion = false;
        let validValue = rawValue;
        if (validValue.startsWith('!') && !validValue.startsWith('!!')) {
          isExclusion = true;
          validValue = validValue.slice(1);
        }

        if (isExclusion) {
          return obj[key] !== validValue;
        }
        if (validValue.startsWith('!!')) {
          return !!obj[key];
        }
        return obj[key] === validValue;
      })
    );
  }
  return objectKeys.includes(field);
};

const enum CrawlerResult {
  Handled = 0,
  ShouldRemove = 1
}

// Helper function to see what fields are being deleted.
const logDeletion = (keychain: Keychain) => {
  // eslint-disable-next-line no-console
  console.log(
    '[filterSanityFields] deleted field',
    keychain.reduce<string>(
      (acc, i) => (typeof i === 'number' ? `${acc}[${i}]` : `${acc ? `${acc}.` : ''}${i}`),
      ''
    )
  );
};

/**
 * Deletes items based on defined filters. Data is not cloned, so deletions are
 * preformed on the provided data directly.
 */
const deleteFilteredSanityFields = <T extends Record<string, any> | Record<string, any>[]>(
  data: T,
  options?: { params: Record<string, any> }
): T => {
  if (!data || (Array.isArray(data) && !data.length)) {
    return data;
  }

  // Stringify for object comparison after filtering. Enable to get various log
  // data in development.
  const developmentLogData =
    // eslint-disable-next-line no-constant-condition
    false && process.env.NODE_ENV === 'development' && options?.params
      ? JSON.stringify(data)
      : null;

  /** Crawler object to recursively traverse the data. */
  const crawler = {
    array: (arr: any[], keychain: Keychain) => {
      for (let i = 0; i < arr.length; i += 1) {
        const item = arr[i];
        if (!item) {
          // eslint-disable-next-line no-continue
          continue;
        }

        keychain.push(i);

        if (Array.isArray(item)) {
          crawler.array(item, keychain);
        } else if (typeof item === 'object') {
          const result = crawler.object(item, keychain);
          if (result === CrawlerResult.ShouldRemove) {
            if (developmentLogData) {
              logDeletion(keychain);
            }
            arr.splice(i, 1);
            i -= 1;
          }
        }

        keychain.pop();
      }
    },
    object: (obj: Record<string, any>, keychain: Keychain) => {
      if (!obj) {
        return CrawlerResult.Handled;
      }
      const keys = Object.keys(obj);

      const objectFilter = SANITY_OBJECT_FILTERS.find(({ matchedFields, requiredFields }) => {
        const hasMatch = matchedFields.every((field) => checkMatchedField(obj, keys, field));
        if (!hasMatch) {
          return false;
        }
        return !requiredFields || !requiredFields.some((key) => keys.includes(key));
      });

      if (objectFilter) {
        // Grab the reference to our cloned data.
        let field: any = data;
        for (let i = 0; i < keychain.length; i += 1) {
          const key = keychain[i];
          if (i < keychain.length - 1) {
            // Assign the next field in the keychain.
            field = field[key];
          } else if (Array.isArray(field) && typeof key === 'number') {
            return CrawlerResult.ShouldRemove;
          } else {
            // Delete the last object in the keychain.obj[key]
            if (developmentLogData) {
              logDeletion(keychain);
            }
            delete field[key];
          }
        }
        return CrawlerResult.Handled;
      }

      const keyFilters = SANITY_KEY_FILTERS.reduce((acc, filter) => {
        const { matchedFields, toRemove } = filter;
        const hasMatch = matchedFields.every((field) => checkMatchedField(obj, keys, field));
        if (hasMatch && toRemove.some((key) => keys.includes(key))) {
          acc.push(filter);
        }
        return acc;
      }, [] as SanityKeyFilter[]);

      if (keyFilters.length) {
        // eslint-disable-next-line no-restricted-syntax
        for (const filter of keyFilters) {
          // Delete the excluded keys from the cloned data.
          // Grab the reference to our cloned data.
          let field: any = data;
          for (let i = 0; i < keychain.length; i += 1) {
            field = field[keychain[i]];
          }

          // eslint-disable-next-line no-restricted-syntax
          for (const key of keys) {
            if (filter.toRemove.includes(key)) {
              if (developmentLogData) {
                logDeletion([...keychain, key]);
              }
              delete field[key];
            }
          }
        }
        return CrawlerResult.Handled;
      }

      // eslint-disable-next-line no-restricted-syntax
      for (const key of keys) {
        const nextItem = obj[key];
        // Ignore null values.
        if (!nextItem) {
          // eslint-disable-next-line no-continue
          continue;
        }

        keychain.push(key);

        if (Array.isArray(nextItem)) {
          crawler.array(nextItem, keychain);
        } else if (typeof nextItem === 'object') {
          crawler.object(nextItem, keychain);
        }

        keychain.pop();
      }
      return CrawlerResult.Handled;
    }
  };

  // Start crawling the data with an empty keychain.
  if (Array.isArray(data)) {
    crawler.array(data, []);
  } else {
    crawler.object(data, []);
  }

  if (developmentLogData) {
    const resultString = JSON.stringify(data);
    const preLength = developmentLogData.length;
    const postLength = resultString.length;
    const chars = preLength - postLength;
    if (chars !== 0) {
      const percentTrimmed = (chars / preLength) * 100;
      const removed = `${percentTrimmed.toFixed(2)}%`;
      // eslint-disable-next-line no-console
      console.log('[filterSanityFields] result', { ...options, removed, chars });
    }
  }

  return data;
};

export async function sanityFetch<R = any>(
  query: string,
  params?: QueryParams | Record<string, any>,
  sanityClient: SanityClient = client,
  { filterFields, disableDiffLogs }: { filterFields?: boolean; disableDiffLogs?: boolean } = {}
): Promise<R> {
  const queryParams = params ? { ...params } : {};

  const locale = (queryParams as QueryParams)?.locale;
  if (locale) {
    queryParams.locale = qaLocalesMap[locale] ?? locale;
  }

  try {
    const response = await sanityClient.fetch(query, queryParams);

    if (filterFields) {
      return deleteFilteredSanityFields(
        response,
        queryParams && !disableDiffLogs ? { params: queryParams } : undefined
      );
    }

    return response;
  } catch (err: any) {
    const rawResponse = err?.response?.text ? await err.response.text() : undefined;

    if (!shouldFilterError(err)) {
      datadogRum.addError(err, {
        context: {
          query,
          params: queryParams,
          message: err?.message,
          status: err?.response?.status,
          response: rawResponse
        }
      });
    }

    throw err;
  }
}

export const sanityApiMutate = async (mutations: {}): Promise<{
  transactionId: string;
  results: { _id: string; _type: string }[];
}> => {
  const url = `https://${SANITY_PROJECT_ID}.api.sanity.io/${apiVersion}/data/mutate/${SANITY_DATASET}`;
  const result = await fetch(url, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${SANITY_AUTH_TOKEN_API_LAYER}`
    },
    body: JSON.stringify(mutations)
  });

  const json = await result.json();

  if (!result.ok) {
    throw new Error(`Failed to mutate Sanity data \n ${JSON.stringify(json)}`);
  }

  return json;
};

export default client;
